/**
 * Sentry error-monitoring + session-replay initialization.
 *
 * Wiring notes:
 * - Toggled at runtime via `/runtime-config` (see docker/runtime-config-
 *   entrypoint.sh), so ops can enable/disable per-deploy WITHOUT rebuilding
 *   the image. Locally it falls back to the build-time VITE_SENTRY_* env vars
 *   (see lib/env.ts).
 * - Disabled unless a DSN is present AND `sentryEnabled` is true. When
 *   disabled, `initSentry` is a no-op and the ~90KB Sentry SDK is never
 *   downloaded — it's a dynamic import gated behind the enable check, so a
 *   build with Sentry off pays effectively nothing.
 * - Called once from main.tsx after the runtime config resolves.
 */

import type { getRuntimeConfigSync } from "@/lib/runtime-config";

type RuntimeConfig = ReturnType<typeof getRuntimeConfigSync>;

let initialized = false;

/**
 * Initialize Sentry from the resolved runtime config. Safe to call once;
 * repeat calls and the disabled state both no-op. When disabled it returns
 * before importing the SDK, so the Sentry chunk is never fetched.
 */
export async function initSentry(config: RuntimeConfig): Promise<void> {
  if (initialized) return;
  if (!config.sentryEnabled || !config.sentryDsn) return;
  initialized = true;

  const Sentry = await import("@sentry/react");

  Sentry.init({
    dsn: config.sentryDsn,
    environment: config.sentryEnvironment,
    // Build id for grouping/releases; source-map upload keys off the same
    // value (SENTRY_RELEASE / vite-plugin release) so traces symbolicate.
    release: import.meta.env.VITE_SENTRY_RELEASE || undefined,
    integrations: [
      // Auto pageload + history-based navigation transactions (TanStack
      // Router navigates via the History API, so this covers route changes).
      Sentry.browserTracingIntegration(),
      // Session Replay — text/inputs masked and media blocked by default so
      // no user PII is recorded (this app handles logistics/customer data).
      Sentry.replayIntegration({
        maskAllText: true,
        maskAllInputs: true,
        blockAllMedia: true,
      }),
    ],
    // Sample 10% of transactions for performance tracing (tune per volume).
    tracesSampleRate: 0.1,
    // Don't record every session (privacy + quota); DO record the full replay
    // whenever an error fires — that's the "watch what the tester hit" case.
    replaysSessionSampleRate: 0.0,
    replaysOnErrorSampleRate: 1.0,
  });
}
